<template>
    <div class="table-responsive">
        <loader v-if="loading && !treatment" />
        <b-table-simple v-else borderless hover class="list">
            <b-thead>
                <b-tr>
                    <b-th>Activity</b-th>
                    <b-th>Type:</b-th>
                    <b-th>Due:</b-th>
                    <b-th>Completed:</b-th>
                    <b-th>Note:</b-th>
                    <b-th></b-th>
                </b-tr>
            </b-thead>
            <b-tbody v-if="activities.length == 0">
                <b-tr>
                    <td colspan="5">
                        <i>No records available</i>
                    </td>
                </b-tr>
            </b-tbody>
            <tr v-for="activity in activities" :key="activity.id">
                <td>{{ activity.activityText.name }}</td>
                <td>{{ activity.activityText.type }}</td>
                <td>
                    {{
                        activity.activityText.isComplete
                            ? "--"
                            : activity.activityText.due
                    }}
                </td>
                <td>
                    {{
                        activity.activityText.isComplete
                            ? activity.activityText.due
                            : "--"
                    }}
                </td>
                <td>{{ activity.activityText.note }}</td>
                <td
                    v-if="
                        treatment.treatment_status[0].status_id >
                        TREATMENT_STATUS.ADMITTED
                    "
                    class="text-right small text-nowrap"
                >
                    <template
                        v-if="
                            _hasAccess('schedule.treatmentboard.update') &&
                            activity.status === 0
                        "
                    >
                        <span
                            class="cursor-pointer"
                            @click="fill(activity, true)"
                        >
                            Complete
                        </span>
                        <span>
                            |
                        </span>
                    </template>
                    <template v-if="activity.status === 1">
                        <span
                            class="cursor-pointer"
                            @click="openModal(activity, true)"
                        >
                            Summary
                        </span>
                        <span>
                            |
                        </span>
                    </template>
                    <template
                        v-if="
                            _hasAccess('schedule.treatmentboard.update') &&
                            activity.status === 3
                        "
                    >
                        <span
                            class="cursor-pointer"
                            @click="
                                edit({
                                    treatment: treatment,
                                    activity: activity
                                })
                            "
                        >
                            Reactivate
                        </span>
                        <span>
                            |
                        </span>
                    </template>
                    <template
                        v-if="
                            _hasAccess('schedule.treatmentboard.update') &&
                            activity.status !== 3
                        "
                    >
                        <span
                            class="cursor-pointer"
                            @click="
                                edit(
                                    {
                                        treatment: treatment,
                                        activity: activity
                                    },
                                    true
                                )
                            "
                        >
                            Edit
                        </span>
                        <span>
                            |
                        </span>
                    </template>
                    <template
                        v-if="
                            _hasAccess('schedule.treatmentboard.create') &&
                            activity.status !== 3
                        "
                    >
                        <span
                            class="cursor-pointer"
                            @click="
                                duplicate({
                                    treatment: treatment,
                                    activity: activity
                                })
                            "
                        >
                            Duplicate
                        </span>
                        <span>
                            |
                        </span>
                    </template>
                    <template
                        v-if="
                            _hasAccess('schedule.treatmentboard.delete') &&
                            activity.status === 0
                        "
                    >
                        <span
                            class="cursor-pointer"
                            @click="
                                deleteModal({
                                    treatment: treatment,
                                    activity: activity
                                })
                            "
                        >
                            Deactivate
                        </span>
                        <span>
                            |
                        </span>
                    </template>
                    <template
                        v-if="
                            _hasAccess('schedule.treatmentboard.delete') &&
                            activity.status === 1
                        "
                    >
                        <span
                            class="cursor-pointer"
                            @click="
                                deleteModal({
                                    treatment: treatment,
                                    activity: activity
                                })
                            "
                        >
                            Delete
                        </span>
                        <span>
                            |
                        </span>
                    </template>
                    <span
                        class="cursor-pointer"
                        @click="
                            activityHistory({
                                treatment: treatment,
                                activity: activity
                            })
                        "
                    >
                        History
                    </span>
                </td>
                <td v-else></td>
            </tr>
        </b-table-simple>
    </div>
</template>

<script>
const { DateTime } = require("luxon");
import HISTORY from "@/constants/history";
import CITEMTYPES from "@/constants/item-types";
import ScheduleHelper from "@/module/schedule/helper";
import TreatmentTimelineMixin from "@/mixins/treatment-timeline-details";
import TreatmentActivityMixin from "@/mixins/treatment-activity-details";

export default {
    mixins: [ScheduleHelper, TreatmentTimelineMixin, TreatmentActivityMixin],
    components: {
        Loader: () => import("@/components/elements/loader-list")
    },
    props: {
        treatmentId: {
            type: String,
            default: null
        },
        dialogs: {
            type: Object,
            default() {
                return {};
            }
        }
    },
    computed: {
        soapId() {
            return this.soapDetails ? this.soapDetails.id : null;
        },
        soapDetails() {
            return this.$store.getters["soap/soapDetails"];
        },
        labelOptions() {
            return this.$store.state.reference["treatment-task-label"];
        },
        routeOptions() {
            return this.$store.state.reference["master-vaccination-route"];
        },
        locationOptions() {
            return this.$store.state.reference["master-vaccination-location"];
        },
        activities() {
            return this.treatment.activities.map(activity =>
                this.getActivityText(activity)
            );
        }
    },
    watch: {},
    data() {
        return {
            loading: true,
            treatment: null
        };
    },
    async mounted() {
        await this.getTreatment();
    },
    destroyed() {},
    methods: {
        async getTreatment() {
            this.loading = true;
            return this.$http
                .get(
                    process.env.BARRACUDA +
                        "schedule/treatment-board/" +
                        this.treatmentId
                )
                .then(response => {
                    if (response.status == 200) {
                        this.treatment = this.getDueForActivity(
                            response.data.response.resources
                        );
                        this.treatment.soap_id = this.soapId;
                    } else {
                        this._message(
                            response.message ||
                                response.statusText ||
                                "Failed to fetch patient's treatment information",
                            "danger"
                        );
                    }
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        getActivityText(oriActivity) {
            // Determine the activity text based on the treatment type
            let selectedActivity = null;
            let activity = { ...oriActivity };
            let isComplete = Boolean(oriActivity.complete_at);
            let referTime = DateTime.fromSeconds(
                oriActivity?.referTime
            ).toFormat(`${this._dateFormat} hh:mm a`);
            switch (activity.treatment_activity_type_id) {
                case this.TREATMENT_TYPE_STR.MEDICAL: {
                    activity =
                        activity.medication.length > 0
                            ? activity.medication[0]
                            : null;
                    let encodedId = activity
                        ? activity?.item?.encoded_id || activity.encoded_id
                        : null;
                    let name = activity
                        ? activity?.item?.name || activity.name
                        : null;

                    selectedActivity = {
                        name: name,
                        encodedId: encodedId,
                        note: oriActivity?.note,
                        route: null,
                        location: null,
                        quantity: activity?.quantity,
                        isComplete: isComplete,
                        due: referTime,
                        type: this.TREATMENT_TYPE[
                            this.TREATMENT_TYPE_STR.MEDICAL
                        ]
                    };
                    if (activity?.item?.type != CITEMTYPES.TYPES.TYPE_LAB) {
                        selectedActivity.route = this.routeOptions.find(
                            option => option.id === activity.route_id
                        );
                        selectedActivity.location = this.locationOptions.find(
                            option => option.id === activity.location_id
                        );
                    }
                    break;
                }
                case this.TREATMENT_TYPE_STR.VITAL: {
                    selectedActivity = {
                        name: activity.vitals
                            .map(vital => vital.master_vital.vital)
                            .join(", "),
                        note: oriActivity?.note,
                        isComplete: isComplete,
                        due: referTime,
                        type: this.TREATMENT_TYPE[this.TREATMENT_TYPE_STR.VITAL]
                    };
                    break;
                }
                case this.TREATMENT_TYPE_STR.OTHER: {
                    if (
                        activity.other_task[0] &&
                        this.labelOptions.length > 0
                    ) {
                        activity = activity.other_task[0];
                        let labelId = activity.label_id ? activity.label_id : 1;
                        let label = this.labelOptions.find(
                            option => option.value === labelId
                        );
                        let name = label.text;
                        if (
                            label.text == "Miscellaneous" &&
                            this.treatmentBoardView != "patient"
                        ) {
                            name = `Misc: ${
                                activity.task == ""
                                    ? "No details"
                                    : activity.task
                            }`;
                        }

                        selectedActivity = {
                            name: name,
                            note: oriActivity?.note,
                            isComplete: isComplete,
                            due: referTime,
                            type: this.TREATMENT_TYPE[
                                this.TREATMENT_TYPE_STR.OTHER
                            ]
                        };
                    }
                    break;
                }
            }
            oriActivity.activityText = selectedActivity;
            return oriActivity;
        },
        triggerTreatmentboardAction(action) {
            this.$emit("activity-action", action, {
                treatment: this.event.treatment,
                activity: this.event.activity
            });
        }
    }
};
</script>
